import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useMediaQuery} from '@mantine/hooks';
import {IconBrain, IconMessageOff, IconSparkles} from '@tabler/icons-react';
import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {ScoutMemoryDrawer} from 'src/app/assistant/Memory/ScoutMemoryDrawer';
import {SidebarLinkAssistant} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {Button} from 'src/design/atoms/Button';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ChatPanel} from 'src/pages/Modules/Assistant/shared/components/ChatPanel';
import {ConversationList} from 'src/pages/Modules/Assistant/shared/components/ConversationList';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useCreateConversationMutation,
  useDeleteConversationMutation,
  useListConversationsQuery,
} from 'src/redux/queries/assistant';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseNumericParam} from 'src/shared/utils/parseNumericParam';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import classes from 'src/pages/Modules/Assistant/shared/components/Assistant.module.css';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';

const mostRecentId = (conversations: {id: number; updatedAt: Date}[]) =>
  [...conversations].sort(
    (a, b) =>
      parseUtcDate(String(b.updatedAt)).getTime() -
      parseUtcDate(String(a.updatedAt)).getTime()
  )[0].id;

export const AssistantPage = () => {
  useSetDocumentTitle('Scout');
  const navigate = useAppNavigate();
  const {goBack: closeChat} = useBackNavigation(PathUtility.Assistant());
  const {conversationId} = useParams();
  const {setAlert} = useAlert();
  const isDesktop = useMediaQuery('(min-width: 48em)', true, {
    getInitialValueInEffect: false,
  });

  const activeId = parseNumericParam(conversationId);
  const [memoryOpened, setMemoryOpened] = useState(false);
  const openMemory = useCallback(() => setMemoryOpened(true), []);

  const {data: conversations, isLoading, isError} = useListConversationsQuery();
  const [createConversation, {isLoading: isCreating}] =
    useCreateConversationMutation();
  const [deleteConversation] = useDeleteConversationMutation();

  const hasConversations = !!conversations && conversations.length > 0;

  // Auto-open the most recent conversation when none is selected; State C is
  // the transient fallback render until this navigation lands.
  useEffect(() => {
    if (
      isDesktop &&
      activeId === undefined &&
      conversations &&
      conversations.length > 0
    ) {
      navigate(PathUtility.Assistant(mostRecentId(conversations)), {
        replace: true,
      });
    }
  }, [isDesktop, activeId, conversations, navigate]);

  const handleNewConversation = async (memoryOff = false) => {
    try {
      const created = await createConversation({memoryOff}).unwrap();
      navigate(PathUtility.Assistant(created.id));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteConversation(id).unwrap();
      if (id === activeId) navigate(PathUtility.Assistant());
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const activeConversation = conversations?.find(c => c.id === activeId);

  const header = (
    <PageHeader>
      <PageHeader.Title
        icon={SidebarLinkAssistant.icon}
        subtitle="Scout is an AI assistant that answers questions about your EHS data">
        Scout
      </PageHeader.Title>
      {activeConversation?.memoryOff && (
        <PageHeader.Badges>
          <PageHeader.Badge color="gray" variant="light">
            Memory off
          </PageHeader.Badge>
        </PageHeader.Badges>
      )}
      <PageHeader.Actions>
        <PageHeader.Action
          variant="secondary"
          icon={<IconBrain size={16} />}
          onClick={openMemory}>
          Memory
        </PageHeader.Action>
        <PageHeader.Action
          variant="secondary"
          icon={<IconMessageOff size={16} />}
          loading={isCreating}
          onClick={() => void handleNewConversation(true)}>
          New chat without memory
        </PageHeader.Action>
      </PageHeader.Actions>
    </PageHeader>
  );

  const memoryDrawer = (
    <ScoutMemoryDrawer
      opened={memoryOpened}
      onClose={() => setMemoryOpened(false)}
    />
  );

  // State B: zero conversations — single full-width empty state, never a split.
  if (!isLoading && !isError && !hasConversations) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Assistant}>
        {header}
        <Center style={{flex: 1, minHeight: '50vh'}}>
          <NoItemsPanel
            canCreate
            icon={<IconSparkles size={48} />}
            heading="Ask Scout, your EHS assistant"
            noItemsText="Scout can find records, summarize inspections and incidents, and answer questions about your safety data. Start a conversation to get going."
            createNode={
              <Button
                onClick={() => void handleNewConversation()}
                loading={isCreating}>
                Start a conversation
              </Button>
            }
          />
        </Center>
        {memoryDrawer}
      </ContentWrapper>
    );
  }

  const list = (
    <ConversationList
      conversations={conversations}
      activeId={activeId}
      isLoading={isLoading}
      isError={isError}
      onSelect={id => navigate(PathUtility.Assistant(id))}
      onDelete={handleDelete}
      onNewConversation={() => void handleNewConversation()}
      isCreating={isCreating}
      fillHeight={isDesktop}
    />
  );

  const rightPane =
    activeId !== undefined ? (
      <ChatPanel
        key={activeId}
        conversationId={activeId}
        title={activeConversation?.title}
        onOpenMemory={openMemory}
      />
    ) : (
      <Center style={{flex: 1, minWidth: 0, minHeight: 0}}>
        {isLoading ? (
          <Text c="dimmed">Select a conversation to begin.</Text>
        ) : (
          <Stack align="center" gap="xs">
            <Text fw={500}>No conversation selected</Text>
            <Text size="sm" c="dimmed">
              Pick one on the left, or start a new conversation.
            </Text>
          </Stack>
        )}
      </Center>
    );

  // Mobile: list is the standard view; an active conversation opens over it in
  // a full-screen modal so the transcript scrolls and the composer pins.
  if (!isDesktop) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Assistant}>
        {header}
        {list}
        {activeId !== undefined && (
          <ChatPanel
            key={activeId}
            conversationId={activeId}
            title={activeConversation?.title}
            fullScreen
            onClose={closeChat}
            onOpenMemory={openMemory}
          />
        )}
        {memoryDrawer}
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Assistant}>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100dvh - 2 * var(--app-shell-padding))',
          minHeight: 0,
          overflow: 'hidden',
        }}>
        <Box style={{flexShrink: 0}}>{header}</Box>
        <Box
          style={{
            display: 'flex',
            gap: 'var(--mantine-spacing-md)',
            flex: 1,
            minHeight: 0,
          }}>
          <Box className={classes.leftPane}>{list}</Box>
          <Box style={{flex: 1, minWidth: 0, display: 'flex'}}>{rightPane}</Box>
        </Box>
      </Box>
      {memoryDrawer}
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.Assistant(':conversationId?'),
  element: <AssistantPage />,
  module: ModuleEnum.Assistant,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
